import _map from 'lodash/map'
import _find from 'lodash/find'
import _get from 'lodash/get'
import _isNil from 'lodash/isNil'
import _filter from 'lodash/filter'
import _orderBy from 'lodash/orderBy'
import _reduce from 'lodash/reduce'
import _assign from 'lodash/assign'
import _cloneDeep from 'lodash/cloneDeep'
import _toString from 'lodash/toString'
import {
  PLAN_SUBSCRIPTION_ANNUAL,
  PLAN_SUBSCRIPTION_MONTHLY,
  PLAN_SUBSCRIPTION_LIVE,
  PLAN_SUBSCRIPTION_UNKNOWN,
} from '.'

export const BILLING_PLAN_GRID_LOCATION_CHECKOUT = 'checkout'
export const BILLING_PLAN_GRID_LOCATION_CHANGE_PLAN = 'planChange'
export const BILLING_PLAN_GRID_LOCATION_GIFT = 'gift'

export const BILLING_PLAN_PROVIDER_PRODUCT_TYPE_RECURRING = 'Recurring'
export const BILLING_PLAN_PROVIDER_PRODUCT_TYPE_GIFT = 'Gift'
export const BILLING_PLAN_PROVIDER_PRODUCT_TYPE_COURSE = 'Course'

export const BILLING_PLAN_TYPE_MONTHLY = 'Monthly'
export const BILLING_PLAN_TYPE_ANNUAL = 'Annual'
export const BILLING_PLAN_TYPE_GAIA_PLUS = 'Gaia+'

export const BILLING_PLAN_PERIOD_WEEK = 'week'
export const BILLING_PLAN_PERIOD_MONTH = 'month'
export const BILLING_PLAN_PERIOD_YEAR = 'year'

export const PLAN_SCHEMA_LEGACY = {
  segments: [],
  heading: '',
  entitlementDays: null,
  shortDetails: '',
  costs: [],
  currencySymbol: '',
  billingPeriodAbbreviation: '',
  name: '',
  billingSchedule: '',
  duration: '',
  currencyIso: '',
  firstPayment: null,
  title: '',
  id: '',
  checkoutEnabled: true,
  billingPeriod: [],
  sku: '',
  valueProps: [],
}

/**
 * Format/transform GraphQL plan data into the legacy plan data shape
 * @param {Array} plans - Array of plans data objects
 * @returns {Array} - Formatted array of plan data objects ordered
 * (monthly first, then annual, and finally gaia+)
 */
export function formatBillingPlans (plansData = {}) {
  if (!plansData) {
    return []
  }

  const plans = _get(plansData, ['billingProviderProductList', 'billingProviderProducts']) || []
  const monthlyPlans = _filter(plans, (p) => _get(p, ['billingProduct', 'name']) === BILLING_PLAN_TYPE_MONTHLY)
  const annualPlans = _filter(plans, (p) => _get(p, ['billingProduct', 'name']) === BILLING_PLAN_TYPE_ANNUAL)
  const gaiaPlusPlans = _filter(plans, (p) => _get(p, ['billingProduct', 'name']) === BILLING_PLAN_TYPE_GAIA_PLUS)
  // order the plans (monthly, annual, gaia+)
  const orderedPlans = [].concat(monthlyPlans, annualPlans, gaiaPlusPlans)

  return _map(orderedPlans, (plan) => {
    const costs = getBillingPlanCosts(plan) || []

    return (_assign(_cloneDeep(PLAN_SCHEMA_LEGACY), {
      id: _get(plan, 'providerFirstSegmentId'),
      sku: _get(plan, 'sku', ''),
      type: _get(plan, ['billingProduct', 'name']),
      legacyType: getBillingPlanLegacyType(plan),
      title: getBillingPlanTextItemValue(plan, 'title'),
      heading: getBillingPlanTextItemValue(plan, 'heading'),
      name: getBillingPlanTextItemValue(plan, 'name'),
      duration: getBillingPlanTextItemValue(plan, 'duration'),
      firstPayment: _get(costs, 0),
      billingPeriodAbbreviation: getBillingPlanTextItemValue(plan, 'billingPeriodAbbreviation'),
      billingSchedule: getBillingPlanTextItemValue(plan, 'billingSchedule'),
      billingPeriod: [
        getBillingPlanTextItemValue(plan, 'billingPeriod'),
      ],
      shortDetails: getBillingPlanTextItemValue(plan, 'shortDetails'),
      valueProps: getBillingPlanTextValueProps(plan),
      currencyIso: getBillingPlanPriceItemValue(plan, 'currency'),
      currencySymbol: getBillingPlanPriceItemValue(plan, 'currencySymbol'),
      checkoutEnabled: _get(plan, 'checkoutEnabled'),
      currentSubscriptionPlanMatchesProduct: _get(plan, 'currentSubscriptionPlanMatchesProduct'),
      nextSubscriptionPlanMatchesProduct: _get(plan, 'nextSubscriptionPlanMatchesProduct'),
      currentSubscriptionPlanMatchesProviderProduct: _get(plan, 'currentSubscriptionPlanMatchesProviderProduct'),
      nextSubscriptionPlanMatchesProviderProduct: _get(plan, 'nextSubscriptionPlanMatchesProviderProduct'),
      percentSavings: getBillingPlanPriceItemValue(plan, 'percentSavings'),
      costs,
      segments: getBillingPlanSegments(plan),
      changePlanAllowed: _get(plan, 'allowChangePlan'),
    }))
  })
}

/**
 * Get plan text item value
 * @param {Object} plan - The billing plan
 * @param {String} key - They object key of the desired text item
 * @returns {String} - The plan text item value
 */
function getBillingPlanTextItemValue (plan, key) {
  if (!plan || !key) {
    return null
  }
  const textItem = _find(_get(plan, 'billingProviderProductText', {}), (i) => _get(i, 'key') === key && _get(i, 'enabled')) || {}
  return _get(textItem, 'value') || ''
}

/**
 * Get plan pricing item
 * @param {Object} plan - The billing plan
 * @param {String} key - They object key of the desired pricing item
 * @returns {String} - The plan pricing item
 */
function getBillingPlanPriceItemValue (plan, key) {
  if (!plan || !key) {
    return null
  }
  const priceItem = _get(plan, ['billingProviderProductPrices', 0], {}) || {}
  return _get(priceItem, key)
}

/**
 * Get the plan enabled value props
 * @param {Object} plan - The billing plan
 * @returns {Array} - The plan value props that are enabled
 */
function getBillingPlanTextValueProps (plan) {
  const valueProps = _filter(_get(plan, 'billingProviderProductText'), (i) => _get(i, 'isValueProp') && _get(i, 'enabled')) || []
  const orderedValueProps = _orderBy(valueProps, ['valuePropOrder'], ['asc'])
  const valuePropArray = _reduce(orderedValueProps, (acc, valueProp) => {
    acc.push(_get(valueProp, 'value'))
    return acc
  }, [])
  return valuePropArray
}

/**
 * Get plan billing segments
 * @param {Object} plan - The billing plan
 * @returns {Array} - The plan billing segments
 */
function getBillingPlanSegments (plan) {
  const {
    termLength,
    termType,
    trialLength,
    trialTermType,
  } = plan
  const currency = getBillingPlanPriceItemValue(plan, 'currency')
  const trialPrice = getBillingPlanPriceItemValue(plan, 'trialSegmentPrice')
  const price = getBillingPlanPriceItemValue(plan, 'price')
  const segments = []

  if (trialLength) {
    segments.push({
      period: getFormattedBillingPlanPeriod(trialTermType, trialLength),
      price: _toString(trialPrice),
      currencyIso: currency,
    })
  }

  segments.push({
    period: getFormattedBillingPlanPeriod(termType, termLength),
    price: _toString(price),
    currencyIso: currency,
  })

  return segments
}

/**
 * Convert the plan type to the legacy plan type
 * @param {Object} plan - The billing plan
 * @returns {String} - The plan legacy type
 */
export function getBillingPlanLegacyType (plan) {
  const realType = _get(plan, ['billingProduct', 'name'])

  switch (realType) {
    case BILLING_PLAN_TYPE_MONTHLY:
      return PLAN_SUBSCRIPTION_MONTHLY
    case BILLING_PLAN_TYPE_ANNUAL:
      return PLAN_SUBSCRIPTION_ANNUAL
    case BILLING_PLAN_TYPE_GAIA_PLUS:
      return PLAN_SUBSCRIPTION_LIVE
    default:
      return PLAN_SUBSCRIPTION_UNKNOWN
  }
}

/**
 * Get the formatted billing period
 * @param {Object} plan - The billing plan
 * @returns {String} - The plan legacy billing period
 */
function getFormattedBillingPlanPeriod (termType, termLength) {
  const periodAbbr = 'P'
  let length = termLength
  let term = 'M'

  // transform "1 year" to "12 months"
  if (termType === BILLING_PLAN_PERIOD_YEAR) {
    length = 12
  }

  // transform "1 week" to "7 days"
  if (termType === BILLING_PLAN_PERIOD_WEEK) {
    term = 'D'
    length = 7 * termLength
  }

  return `${periodAbbr}${length}${term}`
}

/**
 * Get the plan costs
 * @param {Object} plan - The billing plan
 * @returns {Array} - The plan costs
 */
function getBillingPlanCosts (plan) {
  const trialPrice = getBillingPlanPriceItemValue(plan, 'trialSegmentPrice')
  const price = getBillingPlanPriceItemValue(plan, 'price')
  const costs = []

  // allow "zero" to be added as a cost
  // if trialPrice is null or undefined, then there is no trial
  if (!_isNil(trialPrice)) {
    costs.push(_toString(trialPrice))
  }

  costs.push(_toString(price))

  return costs
}
